import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createRiskElement,
  updateRiskElement,
  deleteRiskElement,
  HVARiskElement,
  HVARiskCategory
} from 'services/hvarisk.service';

interface HVARiskElementsTableProps {
  elements: HVARiskElement[];
  categories: HVARiskCategory[];
  onElementsChange: () => Promise<void>;
}

const ElementsChangeContext = createContext<(() => Promise<void>) | undefined>(
  undefined
);

const useElementsChange = () => {
  const context = useContext(ElementsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useElementsChange must be used within a ElementsChangeProvider'
    );
  }
  return context;
};

type ElementActionCellProps = {
  element: HVARiskElement;
  categories: HVARiskCategory[];
};

const ElementActionCell = ({ element, categories }: ElementActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedElement, setSelectedElement] = useState<HVARiskElement | null>(
    null
  );
  const onElementsChange = useElementsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedElement(null);
  };

  const handleShowEditModal = () => {
    setSelectedElement(element);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteRiskElement(element.id);
      console.log(`Risk element with ID: ${element.id} deleted successfully`);
      await onElementsChange();
    } catch (error) {
      console.error(
        `Error deleting risk element with ID: ${element.id}`,
        error
      );
      alert(
        'Error: This risk element may be assigned to campaigns and cannot be deleted.'
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedElement) {
      try {
        await updateRiskElement(selectedElement.id, selectedElement);
        await onElementsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating risk element: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | number | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'categoryid' || name === 'displayorder') {
      newValue = Number(value);
    }

    setSelectedElement(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Risk Element?
          <br />
          <strong>Note:</strong> Elements assigned to campaigns cannot be
          deleted.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Risk Element</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="edit-category-select">Category</Form.Label>
              <Form.Select
                id="edit-category-select"
                name="categoryid"
                value={selectedElement?.categoryid || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="edit-name">Name</Form.Label>
              <Form.Control
                id="edit-name"
                type="text"
                name="name"
                value={selectedElement?.name || ''}
                onChange={handleInputChange}
                required
                maxLength={100}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="edit-description">Description</Form.Label>
              <Form.Control
                id="edit-description"
                as="textarea"
                rows={3}
                name="description"
                value={selectedElement?.description || ''}
                onChange={handleInputChange}
                maxLength={1000}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="edit-aiprompt">AI Prompt</Form.Label>
              <Form.Control
                id="edit-aiprompt"
                as="textarea"
                rows={5}
                name="aiprompt"
                value={selectedElement?.aiprompt || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter the prompt that will guide the AI in detecting this risk..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="edit-displayorder">Display Order</Form.Label>
              <Form.Control
                id="edit-displayorder"
                type="number"
                name="displayorder"
                value={selectedElement?.displayorder || 1}
                onChange={handleInputChange}
                required
                min={1}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <input
                  id="edit-active"
                  type="checkbox"
                  name="active"
                  checked={selectedElement?.active || false}
                  onChange={handleInputChange}
                  className="form-check-input"
                />
                <label className="form-check-label" htmlFor="edit-active">
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Risk Element
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const riskelementsTablecolumns = (
  categories: HVARiskCategory[]
): ColumnDef<HVARiskElement>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'name',
    header: 'Risk Element',
    meta: {
      headerProps: {
        style: { width: '10%' },
        className: 'px-1 fs-10  white-space-nowrap'
      },
      cellProps: { className: 'px-1  white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <ElementActionCell element={original} categories={categories} />;
    },
    meta: {
      headerProps: { style: { width: '5%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'categoryid',
    header: 'Category',
    cell: ({ row }) => {
      const category = categories.find(
        cat => cat.id === row.original.categoryid
      );
      return category?.name || 'Unknown';
    },
    meta: {
      headerProps: { style: { width: '20%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1' }
    }
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const desc = row.original.description || '';
      return desc.length > 100 ? desc.substring(0, 100) + '...' : desc;
    },
    meta: {
      headerProps: { style: { width: '45%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1' }
    }
  },
  {
    accessorKey: 'displayorder',
    header: 'Display Order',
    meta: {
      headerProps: {
        style: { width: '5%' },
        className: 'px-1 fs-10 text-center'
      },
      cellProps: { className: 'px-1 text-center' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ row }) => (
      <Badge bg={row.original.active ? 'success' : 'secondary'}>
        {row.original.active ? 'Active' : 'Inactive'}
      </Badge>
    ),
    meta: {
      headerProps: {
        style: { width: '10%' },
        className: 'px-1 fs-10 text-center'
      },
      cellProps: { className: 'px-1 text-center' }
    }
  }
];

export const HVARiskElementsTable: React.FC<HVARiskElementsTableProps> = ({
  elements,
  categories,
  onElementsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newElement, setNewElement] = useState<Partial<HVARiskElement>>({
    categoryid: 0,
    name: '',
    description: '',
    aiprompt: '',
    displayorder: 1,
    active: true
  });

  const table = useAdvanceTable({
    data: elements,
    columns: riskelementsTablecolumns(categories),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewElement({
      categoryid: 0,
      name: '',
      description: '',
      aiprompt: '',
      displayorder: 1,
      active: true
    });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | number | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'categoryid' || name === 'displayorder') {
      newValue = Number(value);
    }

    setNewElement(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createRiskElement(newElement);
      handleCloseAddModal();
      onElementsChange();
    } catch (error) {
      console.error('Error creating risk element:', error);
      alert('Error creating risk element: ' + error);
    }
  };

  return (
    <ElementsChangeContext.Provider value={onElementsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Risk Elements"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Risk Element
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Risk Element</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="add-category-select">Category</Form.Label>
              <Form.Select
                id="add-category-select"
                name="categoryid"
                value={newElement.categoryid || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="add-name">Name</Form.Label>
              <Form.Control
                id="add-name"
                type="text"
                name="name"
                value={newElement.name || ''}
                onChange={handleInputChange}
                required
                maxLength={100}
                placeholder="e.g., Inappropriate language"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="add-description">Description</Form.Label>
              <Form.Control
                id="add-description"
                as="textarea"
                rows={3}
                name="description"
                value={newElement.description || ''}
                onChange={handleInputChange}
                maxLength={1000}
                placeholder="Detailed description of what this risk element detects..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="add-aiprompt">AI Prompt</Form.Label>
              <Form.Control
                id="add-aiprompt"
                as="textarea"
                rows={5}
                name="aiprompt"
                value={newElement.aiprompt || ''}
                onChange={handleInputChange}
                required
                placeholder="Enter the prompt that will guide the AI in detecting this risk..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="add-displayorder">Display Order</Form.Label>
              <Form.Control
                id="add-displayorder"
                type="number"
                name="displayorder"
                value={newElement.displayorder || 1}
                onChange={handleInputChange}
                required
                min={1}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <input
                  id="add-active"
                  type="checkbox"
                  name="active"
                  checked={newElement.active !== false}
                  onChange={handleInputChange}
                  className="form-check-input"
                />
                <label className="form-check-label" htmlFor="add-active">
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Risk Element
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </ElementsChangeContext.Provider>
  );
};
